/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    getGroups,
    IGroup,
    IGroupDelivery,
    IGroups,
    IOrderDetailsViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    IPriceContext,
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { Address } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ISalesLine } from '../../../components/citta-order-management/get-sales-line';
import { getSalesLines, getSalesLinesInput } from '../../../components/citta-order-management/get-sales-lines';
import { IOrderDetailsConfig, IOrderDetailsProps as IOrderDetailsExtentionProps, IOrderDetailsResources as IOrderDetailsExtentionResources } from '../definition-extensions/order-details.ext.props.autogenerated';
import ShippingAddress from './components/shipping-address';
import { IOrderChannelConfigurationData } from './order-channelConfig.data';
import { PriceComponent } from '@msdyn365-commerce/components';
export interface IOrderInformationExt extends IOrderInformation {
    processingLabelText?: string;
    orderIdLabelText?: string;
}
export const OrderInfomation: React.FC<IOrderInformationExt> = ({
    orderInformationProps,
    salesId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress,
    processingLabelText,
    orderIdLabelText
}) => (
        <Node {...orderInformationProps}>
            {channelName}
            {channelAddress}
            {_renderProcessStatus(salesId, orderIdLabelText!, processingLabelText!)}
            {createdDate}
            {count}
            {amount}
            {channelReferenceId}
        </Node>
    );
function _renderProcessStatus(salesId: React.ReactNode, orderIdLabelText: string, processingLabelText: string): React.ReactNode {
    return (
        <span className={typeof salesId === 'string' ? 'ms-order-details__order-information-tracking-processing' : ''}>
            {typeof salesId === 'string' ? (orderIdLabelText?.concat(' ', processingLabelText!)) : salesId}
        </span>
    );
}
export const SalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
    </Node>
);

export const GroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
    </Node>
);

// tslint:disable
export const Group: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <GroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>
                        {/*@ts-ignore*/}
                        <SalesLine {...salesLine} />
                        {salesLine.salesStatus}
                    </React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);
// tslint:enable

export const Groups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <Group key={index} {...group} />
        ))}
    </Node>
);

export const OrderSummary: React.FC<IOrderSummary> = ({ orderSummaryProps, heading, subtotal, shipping, tax, totalAmount, earnedPoints }) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const Payment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources> = props => {
    const {moduleProps, heading, alert, loading, payment, config, resources } = props;
    const priceContext = _getPriceContext(props);

    if (!props.data.orderHydration.result) {
        return  <Module {...moduleProps}>
            {loading}
        </Module>
    }
    const salesLinesInput = getSalesLinesInput(props, 'ms-order-details', props.data.orderHydration.result!.salesOrder, props.data.orderHydration.result!.products, priceContext);
    const salesLines = salesLinesInput && getSalesLines(salesLinesInput);
    const groups = salesLines
    ? getGroups({
        moduleName:'ms-order-details',
        salesLines,
        resources: resources
    }): undefined;

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            <Node className='ms-order-details__order-overview-container'>
                {_renderOrderOverview(props, salesLines, groups)}
            </Node>
            {groups && <Groups {...groups} />}
            <div className='msc-orderSummary-pamentMethod'>
                {_renderOrderSummary(props, salesLines, groups)}
                {payment && <Payment {...payment} />}
            </div>
            {config && config.emailAddress ?
            <div className='ms-order-details__help'>
                <p className='ms-order-details__help-title'>{resources.needHelpLabel}</p>
                <span className='ms-order-details__help-label'>{"Email: "|| resources.emailAddressLabel}</span>
                <a className='ms-order-details__help-content-number' aria-label={resources.helpLineEmailAriaLabel} href={`mailto:${config.emailAddress}`}>
                    {config.emailAddress}
                </a>
            </div>
            : null}
        </Module>
    );
};

function _renderOrderSummary(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources,
                            salesLines: ISalesLine[] | undefined, groups: IGroups | undefined): React.ReactNode
{
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';

    const {orderSummary} = props;
    if (!orderSummary) {
        return null;
    }
    return (
        <div className='msc-order-summary-wrapper'>
        {orderSummary.heading}
        <div className='msc-order-summary__items'>
            {orderSummary && (
                <>
                    {_renderSubTotal(props)}
                    {isB2B? _renderShippingInclTaxes(props) : _renderB2CShippingInclTaxes(props)}
                    {_renderOtherCharges(props)}
                    {/*hide on us*/}
                    {orderSummary.tax}
                    {_renderTotalOrder(props)}
                    {isB2B? null : _renderTotalSavings(props)}
                </>
            )}
        </div>
    </div>
    );
}

function _renderTotalSavings(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources): JSX.Element | null {
    const DiscountAmount = props.data.orderHydration.result?.salesOrder.DiscountAmount;
    return (
        <p className={`msc-order-summary__line--discount-amount`}>
            {DiscountAmount && DiscountAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>Total savings</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: DiscountAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderSubTotal(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources): JSX.Element | null {
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    var subtotal=0;
    if (isB2B) {
        props.data.orderHydration.result?.salesOrder.SalesLines?.forEach((item)=>{
            subtotal += item.NetAmount!;
           });
    } else {
    props.data.orderHydration.result?.salesOrder.SalesLines?.forEach((item)=>{
     subtotal += item.Price! * item.Quantity!;
    });
    }
     const OrderSubTotal = subtotal;//props.data.checkout.result?.checkoutCart.cart.NetPrice;
     return (
         <p className={`msc-order-summary__line-sub-total`}>
            {OrderSubTotal && OrderSubTotal > 0 ? (
             <>
                     <span className='msc-order-summary__label'>{props.resources.orderSummaryItemsTotalLabel}</span>
                     <PriceComponent
                         data={{
                             price: { CustomerContextualPrice: OrderSubTotal }
                         }}
                         freePriceText={undefined}
                         context={props.context}
                         id={props.id}
                         typeName={props.typeName}
                         className={'msc-order-summary__value'}
                     />
                 </>
             ) : null}
         </p>
     );
 }
 function _renderTotalOrder(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources): JSX.Element | null {
     const OrderTotal = props.data.orderHydration.result?.salesOrder.TotalAmount;
     return (
         <p className={`msc-order-summary__line-order-total`}>
            {OrderTotal && OrderTotal > 0 ? (
             <>
                     <span className='msc-order-summary__label'>{props.resources.orderSummaryGrandTotalLabel}</span>
                     <PriceComponent
                         data={{
                             price: { CustomerContextualPrice: OrderTotal }
                         }}
                         freePriceText={undefined}
                         context={props.context}
                         id={props.id}
                         typeName={props.typeName}
                         className={'msc-order-summary__value'}
                     />
                 </>
             ) : null}
         </p>
     );
 }

function _renderOtherCharges(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources): React.ReactNode | null{
    const chargeAmountInclTax = props.data.orderHydration.result?.salesOrder.ChargeAmount && props.data.orderHydration.result?.salesOrder.ChargeAmount || 0;
    const chargeLines = props.data.orderHydration.result?.salesOrder.ChargeLines;
    const shippingCharges = props.data.orderHydration.result?.salesOrder.ShippingChargeAmount && props.data.orderHydration.result?.salesOrder.ShippingChargeAmount || 0;

    let shippingChargesFromLines = 0;
    let otherChargesAmountFromLines = 0;
    let totalChargeAmountFromLines = 0;
    let chargesOverriden = false;
    let displayOtherChargesAmount = 0;

    const freightChargeCode = props.context.app.config.freightChargeCode;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
            if (chargeLine.ChargeCode != freightChargeCode) {
                otherChargesAmountFromLines += chargeAmountInclTaxLocal;
            }
            if (chargeLine.ChargeCode == freightChargeCode){
                shippingChargesFromLines += chargeAmountInclTaxLocal;
            }
        });
    }

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        totalChargeAmountFromLines = shippingCharges + otherChargesAmountFromLines;
    }
    else{
        totalChargeAmountFromLines = shippingChargesFromLines + otherChargesAmountFromLines;
    }

    chargesOverriden = totalChargeAmountFromLines != chargeAmountInclTax;
    displayOtherChargesAmount = chargesOverriden ? 0 : otherChargesAmountFromLines;

    if (displayOtherChargesAmount == 0) {
        return null;
    }
    return (
        <p className={`msc-order-summary__line-shipment`}>
           {displayOtherChargesAmount && displayOtherChargesAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>Other charges</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: displayOtherChargesAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderShippingInclTaxes(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources): React.ReactNode | null{
    const chargeAmountInclTax = props.data.orderHydration.result?.salesOrder.ChargeAmount && props.data.orderHydration.result?.salesOrder.ChargeAmount || 0;
    const chargeLines = props.data.orderHydration.result?.salesOrder.ChargeLines;
    const shippingCharges = props.data.orderHydration.result?.salesOrder.ShippingChargeAmount && props.data.orderHydration.result?.salesOrder.ShippingChargeAmount || 0;

    let shippingChargesFromLines = 0;
    let otherChargesAmountFromLines = 0;
    let totalChargeAmountFromLines = 0;
    let chargesOverriden = false;
    let displayShippingAmount = 0;

    const freightChargeCode = props.context.app.config.freightChargeCode;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
            if (chargeLine.ChargeCode != freightChargeCode) {
                otherChargesAmountFromLines += chargeAmountInclTaxLocal;
            }
            if (chargeLine.ChargeCode == freightChargeCode){
                shippingChargesFromLines += chargeAmountInclTaxLocal;
            }
        });
    }

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        totalChargeAmountFromLines = shippingCharges + otherChargesAmountFromLines;
    }
    else{
        totalChargeAmountFromLines = shippingChargesFromLines + otherChargesAmountFromLines;
    }

    chargesOverriden = totalChargeAmountFromLines != chargeAmountInclTax;
    displayShippingAmount = chargesOverriden ? chargeAmountInclTax : shippingChargesFromLines;

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        // displayShippingAmount = shippingCharges;
        displayShippingAmount = chargeAmountInclTax;
    }

    if (displayShippingAmount == 0) {
        return props.orderSummary?.shipping;
    }
    return (
        <p className={`msc-order-summary__line-shipment`}>
           {displayShippingAmount && displayShippingAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.shippingCharges}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: displayShippingAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderB2CShippingInclTaxes(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources): React.ReactNode | null{
    const chargeLines = props.data.orderHydration.result?.salesOrder.ChargeLines;
    let chargeAmountInclTax = 0;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
                const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
                chargeAmountInclTax = chargeAmountInclTax + chargeAmountInclTaxLocal;
        });
    }
    if (chargeAmountInclTax == 0) {
        return props.orderSummary?.shipping;
    }
    return (
        <p className={`msc-order-summary__line-shipment`}>
           {chargeAmountInclTax && chargeAmountInclTax > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.shippingCharges}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: chargeAmountInclTax }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderOrderOverview (props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig & IOrderChannelConfigurationData> & IOrderDetailsExtentionResources,
                               salesLines: ISalesLine[] | undefined, groups: IGroups | undefined): React.ReactNode {
    const giftCardItemId: string | undefined = props.data.channelConfiguration.result?.GiftCardItemId;
    const isB2B = props.app.platform.enableCustomerAccountPayment === 'b2b';
    const filteredSalesLines =  salesLines?.filter(s=>s.data.salesLine.ItemId !== giftCardItemId);
    const {orderInfomation, config, resources } = props;
    const {processingLabel, orderIdLabel} = resources;
    if(filteredSalesLines && filteredSalesLines?.length > 0) {
        return (
            <>
                <div className='ms-order-details__order-information-tracking'>
                    {orderInfomation && <OrderInfomation {...orderInfomation} processingLabelText={processingLabel} orderIdLabelText={orderIdLabel} />}
                    {/* {groups && groups.groups[0].delivery.trackingInfo} */}
                    {isB2B? "" : salesLines && _renderDeliveryMode(filteredSalesLines, resources)}
                    {config && config.gstNumber ?
                        <div className='order-details__gst'>
                            <p className='order-details__gst-title'>{resources.companyNameLabel}</p>
                            <p className='order-details__gst-content'>
                                {resources.gstNumberLabel}{' '}
                                <span className='order-details__help-content-number'>
                                    {config.gstNumber}
                                </span>
                            </p>
                        </div>
                        : null}
                </div>
                <div className='ms-order-details__order-shipping-address'>
                    { filteredSalesLines && _renderAddress(filteredSalesLines, resources)}
                </div>
            </>
            );
    }

    return (
            <div className='ms-order-details__order-information-tracking-full'>
                {orderInfomation && <OrderInfomation {...orderInfomation} processingLabelText={processingLabel} orderIdLabelText={orderIdLabel} />}
                {/* {groups && groups.groups[0].delivery.trackingInfo} */}
                {config && config.gstNumber ?
                    <div className='order-details__gst'>
                        <p className='order-details__gst-title'>{resources.companyNameLabel}</p>
                        <p className='order-details__gst-content'>
                            {resources.gstNumberLabel}{' '}
                            <span className='order-details__help-content-number'>
                                {config.gstNumber}
                            </span>
                        </p>
                    </div>
                    : null}
            </div>
        );
}

function _getPriceContext(props: IOrderDetailsViewProps & IOrderDetailsExtentionProps<IOrderDetailsConfig> & IOrderDetailsExtentionResources): IPriceContext {
    const{id, typeName, context, telemetry,  resources:{ freePriceText }} = props;
    return {
        id,
        typeName,
        context,
        telemetry,
        freePriceText
    };
}
function _renderAddress(salesLines: ISalesLine[], resources:IOrderDetailsExtentionResources): React.ReactNode {
    const shippingAddress = _getShippingAddress(salesLines);
    return (<ShippingAddress address={shippingAddress} resource={{ shipToLabel: resources.shipToLabel}} />);
}
function _renderDeliveryMode(salesLines: ISalesLine[], resources:IOrderDetailsExtentionResources): React.ReactNode {
    return (
        <p className='order-details__deliveryMode'>
            {resources.deliveryMode}{': '}
            <span className='order-details__help-content-number'>
            {salesLines[0].data.deliveryType === 'ship' ? 'Shipping' : 'Click & Collect' }
            </span>
        </p>
    );
}
function _getShippingAddress(salesLinesProps: ISalesLine[]): Address {
    return salesLinesProps[0].data.salesLine.ShippingAddress || {};
}

export default OrderDetailsView;
